import React, { useState, useEffect } from 'react';
import { AutoComplete, Tag, Typography } from 'antd';
import {
  SearchOutlined,
  ClockCircleOutlined,
  FireOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBrands } from '../store/slices/brandSlice';
import { debounce } from 'lodash';

const { Text } = Typography;

interface SearchSuggestionsProps {
  onSearch: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

interface SearchSuggestion {
  value: string;
  label: React.ReactNode;
  type: 'brand' | 'category' | 'history' | 'trending';
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  onSearch,
  placeholder = '搜索品牌、分类...',
  style,
}) => {
  const dispatch = useAppDispatch();
  const { brands } = useAppSelector(state => state.brands);
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  // 从localStorage加载搜索历史
  useEffect(() => {
    const history = localStorage.getItem('searchHistory');
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  // 保存搜索历史到localStorage
  const saveSearchHistory = (query: string) => {
    if (!query.trim()) return;

    const newHistory = [
      query,
      ...searchHistory.filter(item => item !== query),
    ].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  // 防抖搜索建议
  const debouncedGetSuggestions = debounce(async (value: string) => {
    if (!value.trim()) {
      // 显示搜索历史和热门搜索
      const historySuggestions: SearchSuggestion[] = searchHistory
        .slice(0, 5)
        .map(item => ({
          value: item,
          type: 'history',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockCircleOutlined style={{ color: '#999' }} />
              <Text>{item}</Text>
            </div>
          ),
        }));

      const trendingSuggestions: SearchSuggestion[] = [
        '可持续时尚',
        '环保化妆品',
        '公平贸易',
        '零残忍',
        '有机食品',
      ].map(item => ({
        value: item,
        type: 'trending',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FireOutlined style={{ color: '#ff4d4f' }} />
            <Text>{item}</Text>
            <Tag
              style={{
                color: 'var(--app-primary-color)',
                borderColor: 'var(--app-primary-color)',
                backgroundColor: 'transparent',
              }}
            >
              热门
            </Tag>
          </div>
        ),
      }));

      setSuggestions([...historySuggestions, ...trendingSuggestions]);
      return;
    }

    try {
      // 搜索品牌
      const response = await dispatch(
        fetchBrands({
          search: value,
          limit: 5,
          offset: 0,
        })
      ).unwrap();

      const brandSuggestions: SearchSuggestion[] = response.data.map(
        (brand: any) => ({
          value: brand.name,
          type: 'brand',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SearchOutlined style={{ color: 'var(--app-primary-color)' }} />
              <div>
                <Text strong>{brand.name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {brand.categories.join(', ')}
                </Text>
              </div>
            </div>
          ),
        })
      );

      // 分类建议
      const categories = ['时尚', '化妆品', '食品', '家居', '电子产品'];
      const categorySuggestions: SearchSuggestion[] = categories
        .filter(cat => cat.toLowerCase().includes(value.toLowerCase()))
        .map(cat => ({
          value: cat,
          type: 'category',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag
                style={{
                  color: 'var(--app-primary-color)',
                  borderColor: 'var(--app-primary-color)',
                  backgroundColor: 'transparent',
                }}
              >
                {cat}
              </Tag>
              <Text>分类</Text>
            </div>
          ),
        }));

      setSuggestions([...brandSuggestions, ...categorySuggestions]);
    } catch (error) {
      console.error('获取搜索建议失败:', error);
    }
  }, 300);

  const handleSearch = (value: string) => {
    if (value.trim()) {
      saveSearchHistory(value.trim());
      onSearch(value.trim());
    }
  };

  const handleSelect = (value: string) => {
    setSearchValue(value);
    handleSearch(value);
  };

  const handleInputChange = (value: string) => {
    setSearchValue(value);
    debouncedGetSuggestions(value);
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
    setSuggestions([]);
  };

  return (
    <div style={style}>
      <AutoComplete
        value={searchValue}
        options={suggestions}
        onSelect={handleSelect}
        onSearch={handleInputChange}
        placeholder={placeholder}
        style={{ width: '100%' }}
        size="large"
        allowClear
        onClear={() => setSearchValue('')}
        popupRender={menu => (
          <div>
            {menu}
            {searchHistory.length > 0 && (
              <div
                style={{
                  padding: '8px 12px',
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  搜索历史
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: '12px', cursor: 'pointer' }}
                  onClick={clearSearchHistory}
                >
                  清除
                </Text>
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default SearchSuggestions;
